import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { exportBrandedPdf } from '../utils/brandedPdf';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import { FaSearch, FaTimes, FaInfoCircle, FaBalanceScale, FaUser, FaBuilding, FaUpload, FaDownload } from 'react-icons/fa';
import { similarity } from '../utils/stringSimilarity';
import '../Pages.css';
import '../Pips/Pips.css';
import './UnSanctions.css';
import { formatDate } from '../utils/dateFormat';

const hasPermission = (user, name) =>
  !!user && (
    user.is_system_admin === true ||
    (Array.isArray(user.permissions) && user.permissions.includes(name))
  );

// dd/mm/yyyy with HH:MM time. Returns '—' for falsy input.
const formatLastUpdated = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy}, ${hh}:${mi}`;
};

// "Privileged" UN screening user — system admin OR a user without an organisation.
// Drives whether quota / score-slider / org-scoped UI is shown.
const isUnscopedSearcher = (user) =>
  !!user && (
    user.is_system_admin === true ||
    !user.organisation_id
  );

const FILTERS = [
  { id: 'all',          label: 'All',          icon: <FaBalanceScale /> },
  { id: 'individuals',  label: 'Individuals',  icon: <FaUser /> },
  { id: 'entities',     label: 'Entities',     icon: <FaBuilding /> }
];

function UnSanctions() {
  const { user } = useContext(UserContext);

  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [individuals, setIndividuals] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchSubmitted, setSearchSubmitted] = useState(false);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  // The record whose details are open in the View details pop-up
  const [detailRecord, setDetailRecord] = useState(null);

  // Esc closes the pop-up
  useEffect(() => {
    if (!detailRecord) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDetailRecord(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [detailRecord]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [isPrivilegedUser, setIsPrivilegedUser] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [minSimilarity, setMinSimilarity] = useState(0);
  const [searchLimitInfo, setSearchLimitInfo] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkSearchInfo, setBulkSearchInfo] = useState(null);
  // Per-row selection (keys are `${kind}-${id}` strings) — mirrors PIP's
  // selectedPipIds and is used to scope Export PDF to the chosen rows.
  const [selectedIds, setSelectedIds] = useState([]);

  const searchInputRef = useRef(null);

  const recordKey = (r) => `${r._kind}-${r.id}`;

  const toggleSelectOne = (key, checked) => {
    setSelectedIds(prev => checked ? [...new Set([...prev, key])] : prev.filter(k => k !== key));
  };

  const toggleSelectPage = (pageItems, checked) => {
    const pageKeys = pageItems.map(recordKey);
    setSelectedIds(prev => checked
      ? [...new Set([...prev, ...pageKeys])]
      : prev.filter(k => !pageKeys.includes(k)));
  };

  useEffect(() => {
    setIsPrivilegedUser(isUnscopedSearcher(user));
  }, [user]);

  const canSearch = hasPermission(user, 'un_sanctions.search');
  // Bulk search is tied to the org's package quota, so hide it for admins
  // (no quota / direct access) and users without an organisation.
  const canDoBulkSearch = !!user && !isUnscopedSearcher(user) && !!user.organisation_id;

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/un-sanctions/stats');
      setStats(res.data);
    } catch (e) { console.error('UN stats error:', e.message); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get('/un-sanctions/history?limit=8');
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error('UN history error:', e.message); }
  }, []);

  useEffect(() => {
    if (!canSearch) return;
    fetchStats();
    fetchHistory();
    if (searchInputRef.current) searchInputRef.current.focus();
  }, [canSearch, fetchStats, fetchHistory]);

  // Prefetch the shared search-limit info for non-privileged users so the stats
  // cards and alert banners can render before any search runs.
  useEffect(() => {
    if (!canSearch || isPrivilegedUser) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/un-sanctions?limit=1');
        if (!cancelled && res.meta?.searchLimitInfo) {
          setSearchLimitInfo(res.meta.searchLimitInfo);
        }
      } catch (e) {
        if (!cancelled) console.error('UN limit prefetch error:', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [canSearch, isPrivilegedUser]);

  // Admins (privileged users) auto-load all sanctioned records on mount,
  // mirroring how the PIP page behaves for privileged users.
  useEffect(() => {
    if (isPrivilegedUser && canSearch && !searchSubmitted) {
      runSearch('', 'all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivilegedUser, canSearch]);

  const runSearch = async (overrideTerm) => {
    const term = overrideTerm !== undefined ? overrideTerm : searchTerm;

    // Non-privileged users cannot run an empty search (mirrors PIP behavior)
    if (!term.trim() && !isPrivilegedUser) return;

    setLoading(true);
    setError(null);
    setSearchSubmitted(true);
    try {
      const params = new URLSearchParams();
      if (term.trim()) params.set('query', term.trim());
      // Always fetch both types from the server; the individual/entity chips filter
      // the loaded set client-side so both counts stay meaningful when filtering.
      params.set('filter', 'all');
      if (isPrivilegedUser) params.set('limit', '5000');
      const res = await axios.get(`/un-sanctions?${params.toString()}`);
      const data = res.data || {};
      setIndividuals(Array.isArray(data.individuals) ? data.individuals : []);
      setEntities(Array.isArray(data.entities) ? data.entities : []);
      setBulkSearchInfo(null);
      setSelectedIds([]);
      if (res.meta?.searchLimitInfo) setSearchLimitInfo(res.meta.searchLimitInfo);
      setCurrentPage(1);
      fetchStats();
      fetchHistory();
    } catch (err) {
      setError(err.message || 'Search failed');
      setIndividuals([]);
      setEntities([]);
    } finally {
      setLoading(false);
    }
  };

  // Download the bulk-search CSV template (single column: full_name).
  const downloadBulkTemplate = () => {
    const csv = 'full_name\nOsama Bin Laden\nAl-Qaida\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'un_sanctions_bulk_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    setBulkError('');
    if (!bulkFile) {
      setBulkError('Please select a CSV or Excel file.');
      return;
    }
    const ext = bulkFile.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      setBulkError('Invalid file format. Upload .csv, .xlsx, or .xls.');
      return;
    }

    const formData = new FormData();
    formData.append('file', bulkFile);

    try {
      setBulkLoading(true);
      const res = await axios.post('/un-sanctions/search/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const data = res.data || {};
      setIndividuals(Array.isArray(data.individuals) ? data.individuals : []);
      setEntities(Array.isArray(data.entities) ? data.entities : []);
      setSearchSubmitted(true);
      setSearchTerm(`Bulk: ${res.meta?.bulkSearchInfo?.totalSearched || 0} names`);
      setActiveFilter('all');
      setCurrentPage(1);
      if (res.meta?.searchLimitInfo) setSearchLimitInfo(res.meta.searchLimitInfo);
      if (res.meta?.bulkSearchInfo) setBulkSearchInfo(res.meta.bulkSearchInfo);
      setShowBulkModal(false);
      setBulkFile(null);
      fetchStats();
      fetchHistory();
    } catch (err) {
      setBulkError(err.message || 'Bulk search failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') runSearch(); };

  const clearSearch = () => {
    setSearchTerm('');
    setTableSearch('');
    setMinSimilarity(0);
    setIndividuals([]);
    setEntities([]);
    setSearchSubmitted(false);
    setActiveFilter('all');
    setCurrentPage(1);
    setDetailRecord(null);
    setSelectedIds([]);
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  // Filter switching is client-side only — the server always returns both types,
  // and `combinedAll` slices by activeFilter. This keeps both count chips honest.
  const applyFilter = (filter) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  // Recent-search chip: fills in the search box only; the user runs it.
  const applySuggestion = (q, filter) => {
    setSearchTerm(q);
    setActiveFilter(filter || 'all');
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  // Export UN sanctions results as a branded PDF. If the user has explicitly
  // checked any rows we export only those; otherwise we export the full
  // (currently visible) result set — same UX as the PIP Export PDF.
  const exportPDF = async () => {
    const exportRecords = selectedIds.length > 0
      ? combined.filter(r => selectedIds.includes(recordKey(r)))
      : combined;
    if (exportRecords.length === 0) return;

    await exportBrandedPdf({
      title: 'UN CONSOLIDATED SANCTIONS — SCREENING REPORT',
      user,
      searchValue: [
        searchTerm ? `"${searchTerm}"` : 'All records',
        activeFilter !== 'all' && `Filter: ${activeFilter}`,
        selectedIds.length > 0 ? `${exportRecords.length} selected` : null
      ].filter(Boolean).join('  ·  '),
      fileName: 'UN_Sanctions_Report',
      columns: [
        { header: 'Type', width: 22 },
        { header: 'Name', width: 50, bold: true },
        { header: 'Ref #', width: 22 },
        { header: 'List', width: 28 },
        { header: 'Listed On', width: 22 },
        { header: 'Nationality', width: 32 },
        { header: 'Designation', width: 50 },
        { header: 'Aliases', width: 50 }
      ],
      rows: exportRecords.map(r => {
        const isInd = r._kind === 'individual';
        const name = isInd
          ? [r.first_name, r.second_name, r.third_name, r.fourth_name].filter(Boolean).join(' ')
          : r.first_name;
        return [
          isInd ? 'Individual' : 'Entity',
          name || 'Unnamed',
          r.reference_number,
          r.un_list_type || r.list_type,
          formatDate(r.listed_on, ''),
          isInd && Array.isArray(r.nationalities) ? r.nationalities.join(', ') : '',
          isInd && Array.isArray(r.designations) ? r.designations.join('; ') : '',
          Array.isArray(r.aliases) ? r.aliases.map(a => a.alias_name).filter(Boolean).join('; ') : ''
        ];
      })
    });
  };

  // Combine results into a flat ordered list, then apply the client-side table filter
  // (used by admin to refine the loaded list without re-hitting the server).
  const combinedAll = [
    ...(activeFilter === 'entities' ? [] : individuals.map(r => ({ ...r, _kind: 'individual' }))),
    ...(activeFilter === 'individuals' ? [] : entities.map(r => ({ ...r, _kind: 'entity' })))
  ];

  const tableSearchLc = tableSearch.trim().toLowerCase();
  const combinedFiltered = tableSearchLc
    ? combinedAll.filter(r => {
        const isInd = r._kind === 'individual';
        const name = isInd
          ? [r.first_name, r.second_name, r.third_name, r.fourth_name].filter(Boolean).join(' ')
          : r.first_name || '';
        const aliasNames = Array.isArray(r.aliases) ? r.aliases.map(a => a.alias_name || '').join(' ') : '';
        const haystack = [name, r.reference_number, r.un_list_type, aliasNames]
          .filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(tableSearchLc);
      })
    : combinedAll;

  // Similarity scoring against the search term — same pattern as PIP.
  // Score gating only applies for org users (the slider is hidden for everyone else).
  const showScoreSlider = !isPrivilegedUser && !!user?.organisation_id;
  const proximityBase = (tableSearch.trim() || searchTerm.trim()).toLowerCase();
  const scoreRecord = (r) => {
    if (!proximityBase) return 100;
    const isInd = r._kind === 'individual';
    const name = (isInd
      ? [r.first_name, r.second_name, r.third_name, r.fourth_name].filter(Boolean).join(' ')
      : r.first_name || '').toLowerCase();
    if (name === proximityBase || name.includes(proximityBase) || proximityBase.includes(name)) return 100;
    let best = similarity(proximityBase, name);
    if (Array.isArray(r.aliases)) {
      for (const a of r.aliases) {
        const alias = (a.alias_name || '').toLowerCase();
        if (!alias) continue;
        if (alias === proximityBase || alias.includes(proximityBase) || proximityBase.includes(alias)) return 100;
        const s = similarity(proximityBase, alias);
        if (s > best) best = s;
      }
    }
    return best;
  };

  const combined = (showScoreSlider && minSimilarity > 0)
    ? combinedFiltered
        .map(r => ({ ...r, _similarityScore: scoreRecord(r) }))
        .filter(r => r._similarityScore >= minSimilarity)
    : combinedFiltered;

  const totalPages = Math.max(1, Math.ceil(combined.length / itemsPerPage));
  const pageItems = combined.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const renderDob = (dob) => {
    const parts = [];
    if (dob.type_of_date) parts.push(dob.type_of_date);
    if (dob.date) parts.push(dob.date);
    if (dob.year) parts.push(dob.year);
    if (dob.from_year || dob.to_year) parts.push(`${dob.from_year || '?'}–${dob.to_year || '?'}`);
    return parts.filter(Boolean).join(' • ') || '—';
  };

  const renderAddress = (a) => {
    return [a.street, a.city, a.state_province, a.zip_code, a.country].filter(Boolean).join(', ') || '—';
  };

  // Card body: the short version on the card, everything in the View details
  // pop-up (`full`).
  const renderBody = (record, full) => {
    const isInd = record._kind === 'individual';
    return (
      <>
        {isInd && record.nationalities?.length > 0 && (
          <div className="un-row">
            <span className="un-row__label">Nationality</span>
            <span className="un-row__value">{record.nationalities.join(', ')}</span>
          </div>
        )}
        {isInd && record.designations?.length > 0 && (
          <div className="un-row">
            <span className="un-row__label">Designation</span>
            <span className="un-row__value">{record.designations.join('; ')}</span>
          </div>
        )}
        {record.comments1 && (
          <div className="un-row">
            <span className="un-row__label">Comments</span>
            <span className="un-row__value un-row__value--longtext">
              {full || record.comments1.length < 220
                ? record.comments1
                : record.comments1.slice(0, 220) + '…'}
            </span>
          </div>
        )}

        {full && (
          <>
            {record.aliases?.length > 0 && (
              <div className="un-row">
                <span className="un-row__label">Aliases ({record.aliases.length})</span>
                <span className="un-row__value">
                  {record.aliases.map((a, i) => (
                    <span key={i} className="un-alias">
                      <span className={`un-alias__quality un-alias__quality--${(a.quality || '').toLowerCase()}`}>
                        {a.quality || '?'}
                      </span>
                      {a.alias_name}
                    </span>
                  ))}
                </span>
              </div>
            )}
            {record.addresses?.length > 0 && (
              <div className="un-row">
                <span className="un-row__label">Addresses</span>
                <span className="un-row__value">
                  {record.addresses.map((a, i) => <div key={i}>{renderAddress(a)}{a.note ? ` (${a.note})` : ''}</div>)}
                </span>
              </div>
            )}
            {isInd && record.dobs?.length > 0 && (
              <div className="un-row">
                <span className="un-row__label">Date of birth</span>
                <span className="un-row__value">
                  {record.dobs.map((d, i) => <div key={i}>{renderDob(d)}{d.note ? ` — ${d.note}` : ''}</div>)}
                </span>
              </div>
            )}
            {isInd && record.pobs?.length > 0 && (
              <div className="un-row">
                <span className="un-row__label">Place of birth</span>
                <span className="un-row__value">
                  {record.pobs.map((p, i) => <div key={i}>{[p.city, p.state_province, p.country].filter(Boolean).join(', ') || '—'}</div>)}
                </span>
              </div>
            )}
            {isInd && record.documents?.length > 0 && (
              <div className="un-row">
                <span className="un-row__label">Documents</span>
                <span className="un-row__value">
                  {record.documents.map((d, i) => (
                    <div key={i}>
                      <strong>{d.type_of_document || 'Doc'}</strong>
                      {d.number ? `: ${d.number}` : ''}
                      {d.issuing_country ? ` (${d.issuing_country})` : ''}
                      {d.note ? ` — ${d.note}` : ''}
                    </div>
                  ))}
                </span>
              </div>
            )}
            {record.last_updates?.length > 0 && (
              <div className="un-row">
                <span className="un-row__label">Last updated</span>
                <span className="un-row__value">{record.last_updates.join(', ')}</span>
              </div>
            )}
            <div className="un-row">
              <span className="un-row__label">Source ID</span>
              <span className="un-row__value">{record.dataid}</span>
            </div>
          </>
        )}
      </>
    );
  };

  const renderCard = (record, index = 0) => {
    const isInd = record._kind === 'individual';
    const isAlt = index % 2 === 1;
    const key = recordKey(record);
    const checked = selectedIds.includes(key);
    const title = isInd
      ? [record.first_name, record.second_name, record.third_name, record.fourth_name].filter(Boolean).join(' ')
      : record.first_name;

    return (
      <div key={key} className={`un-card un-card--${record._kind}${isAlt ? ' un-card--alt' : ''}`}>
        <div className="un-card__header">
          <div className="un-card__title-row">
            <input
              type="checkbox"
              checked={checked}
              onChange={e => toggleSelectOne(key, e.target.checked)}
              title="Select for export"
              style={{ marginRight: '0.25rem', cursor: 'pointer' }}
            />
            <span className={`un-card__badge un-card__badge--${record._kind}`}>
              {isInd ? <FaUser /> : <FaBuilding />}
              {isInd ? 'Individual' : 'Entity'}
            </span>
            <h3 className="un-card__title">{title || 'Unnamed'}</h3>
          </div>
          <div className="un-card__meta">
            <span className="un-meta__pill">Ref: {record.reference_number || '—'}</span>
            <span className="un-meta__pill">{record.un_list_type || record.list_type || '—'}</span>
            <span className="un-meta__pill">Listed: {formatDate(record.listed_on)}</span>
            {record.has_interpol_link === 'YES' && (
              <a href={record.interpol_link} target="_blank" rel="noopener noreferrer" className="un-meta__pill un-meta__pill--link">
                INTERPOL notice ↗
              </a>
            )}
          </div>
        </div>

        <div className="un-card__body">
          {renderBody(record, false)}
        </div>

        <div className="un-card__footer">
          <button
            type="button"
            className="view-details-btn"
            onClick={() => setDetailRecord(record)}
          >
            View details
          </button>
        </div>
      </div>
    );
  };

  // Gate: users without un_sanctions.search permission see a friendly message
  // (the backend will return 403 on /stats, /history, and /un-sanctions anyway).
  if (user && !canSearch) {
    return (
      <div className="page-container un-sanctions-page">
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '2rem' }}>
          <span className="alert-banner__body">
            <FaInfoCircle /> You don't have permission to access the UN Sanctions screening module.
            Ask your administrator to grant you the <code>un_sanctions.search</code> permission.
          </span>
        </div>
      </div>
    );
  }

  // Convenience flags for limit gating (non-privileged only).
  const singleAtZero = !isPrivilegedUser && searchLimitInfo
    && searchLimitInfo.screeningLimit !== null
    && searchLimitInfo.screeningsLeft === 0;
  const bulkAtZero = !isPrivilegedUser && searchLimitInfo
    && searchLimitInfo.batchScreeningLimit !== null
    && searchLimitInfo.batchScreeningsLeft === 0;

  return (
    <div className="page-container un-sanctions-page">
      {/* Limit alerts — shared with PIP; mirror PIP's alert styling. */}
      {!isPrivilegedUser && searchLimitInfo && (
        <>
          {!singleAtZero && searchLimitInfo.screeningLimit !== null && searchLimitInfo.screeningsLeft > 0 && searchLimitInfo.screeningsLeft <= 5 && (
            <div className={`alert-banner alert-banner--${searchLimitInfo.screeningsLeft <= 2 ? 'danger' : 'warning'}`} style={{ marginBottom: '0.75rem' }}>
              <span className="alert-banner__body">
                <FaInfoCircle /> Only {searchLimitInfo.screeningsLeft} search{searchLimitInfo.screeningsLeft === 1 ? '' : 'es'} remaining.
              </span>
            </div>
          )}
        </>
      )}

      {/* Stats — always rendered (matches PIPs page). Values show '—' until
          the /un-sanctions/stats endpoint succeeds so layout stays consistent
          even when the schema isn't loaded on the DB yet. */}
      {user && (
        <div className="stats-section">
          {/* Shared stat-card layout (Pips.css): lead card on the left, the rest compact beside it */}
          <div className="stats-layout">
            <div className="stat-card stats-layout__lead stat-card--teal">
              <div className="stat-label">Source</div>
              <div className="stat-value stat-value--teal" style={{ fontSize: '1rem' }}>UN Consolidated List</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary,#607d8b)', marginTop: '0.35rem' }}>
                Last updated: <strong>{formatLastUpdated(stats?.last_updated)}</strong>
              </div>
            </div>
            <div className="stats-layout__rest">
          <div className="stats-grid">
            <div className="stat-card stat-card--blue">
              <div className="stat-label">Individuals</div>
              <div className="stat-value stat-value--blue">{stats ? stats.total_individuals : '—'}</div>
            </div>
            <div className="stat-card stat-card--purple">
              <div className="stat-label">Entities</div>
              <div className="stat-value stat-value--purple">{stats ? stats.total_entities : '—'}</div>
            </div>
            <div className="stat-card stat-card--orange">
              <div className="stat-label">My searches</div>
              <div className="stat-value stat-value--orange">{stats ? stats.my_searches : '—'}</div>
            </div>
          </div>

          {/* Shared-quota cards for non-privileged users — same layout as PIP. */}
          {!isPrivilegedUser && searchLimitInfo && (
            <>
              <div className="stats-grid">
                <div className="stat-card stat-card--teal">
                  <div className="stat-label">Package Name</div>
                  <div className="stat-value stat-value--teal">{searchLimitInfo.package}</div>
                </div>
                <div className="stat-card stat-card--blue">
                  <div className="stat-label">Single Screening Limit</div>
                  <div className="stat-value stat-value--blue">
                    {searchLimitInfo.screeningLimit === null ? 'Unlimited' : searchLimitInfo.screeningLimit}
                  </div>
                </div>
                <div className="stat-card stat-card--orange">
                  <div className="stat-label">Single Screenings Done</div>
                  <div className="stat-value stat-value--orange">{searchLimitInfo.screeningsDone}</div>
                </div>
                <div className="stat-card stat-card--teal">
                  <div className="stat-label">Single Screenings Left</div>
                  <div className={`stat-value ${singleAtZero ? 'stat-value--red' : 'stat-value--green'}`}>
                    {searchLimitInfo.screeningsLeft === 'Unlimited' ? 'Unlimited' : searchLimitInfo.screeningsLeft}
                  </div>
                </div>
              </div>
              <div className="stats-grid">
                <div className="stat-card stat-card--purple">
                  <div className="stat-label">Bulk Screening Limit</div>
                  <div className="stat-value stat-value--purple">
                    {searchLimitInfo.batchScreeningLimit === null || searchLimitInfo.batchScreeningLimit === undefined
                      ? 'Unlimited'
                      : searchLimitInfo.batchScreeningLimit}
                  </div>
                </div>
                <div className="stat-card stat-card--orange">
                  <div className="stat-label">Bulk Screenings Done</div>
                  <div className="stat-value stat-value--orange">{searchLimitInfo.batchScreeningsDone || 0}</div>
                </div>
                <div className="stat-card stat-card--teal">
                  <div className="stat-label">Bulk Screenings Left</div>
                  <div className={`stat-value ${bulkAtZero ? 'stat-value--red' : 'stat-value--green'}`}>
                    {searchLimitInfo.batchScreeningsLeft === 'Unlimited' ? 'Unlimited' : searchLimitInfo.batchScreeningsLeft}
                  </div>
                </div>
                <div className="stat-card stat-card--brown">
                  <div className="stat-label">Total Searches</div>
                  <div className="stat-value stat-value--teal">
                    {(searchLimitInfo.screeningsDone || 0) + (searchLimitInfo.batchScreeningsDone || 0)}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
          </div>
          {/* Limit messages — shown once, in the stats panel (same as PIPs) */}
          {singleAtZero && (
            <div className="stat-limit-warning">
              <strong>Warning:</strong> This organisation has reached the maximum number of single searches allowed by their package.
              Users will not be able to perform additional single searches until the package is upgraded.
            </div>
          )}
          {bulkAtZero && (
            <div className="stat-limit-warning">
              <strong>Warning:</strong> This organisation has reached the maximum number of bulk searches allowed by their package.
              Users will not be able to perform additional bulk searches until the package is upgraded.
            </div>
          )}
          {!isPrivilegedUser && searchLimitInfo && searchLimitInfo.batchScreeningLimit !== null &&
            searchLimitInfo.batchScreeningsLeft > 0 && searchLimitInfo.batchScreeningsLeft <= 5 && (
              <div className="stat-limit-caution">
                <strong>Notice:</strong> This organisation has only {searchLimitInfo.batchScreeningsLeft} bulk search
                {searchLimitInfo.batchScreeningsLeft === 1 ? '' : 'es'} remaining.
              </div>
            )}
        </div>
      )}

      {/* Admin search bar — simple input like the PIP admin view (no big teal hero). */}
      {isPrivilegedUser && (
        <div style={{ marginBottom: '2rem' }}>
          <div className="table-controls">
            <input
              type="text"
              placeholder="Search UN Sanctions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              className="search-filter"
              ref={searchInputRef}
            />
            <button
              className="search-button"
              onClick={() => runSearch()}
              style={{ marginLeft: '10px', padding: '8px 20px' }}
            >
              Search
            </button>
            {searchTerm && (
              <button className="clear-search-button" onClick={clearSearch} style={{ marginLeft: '10px' }}>
                <FaTimes /> Clear
              </button>
            )}
            {canDoBulkSearch && (
              <button
                className="export-button"
                onClick={() => { setBulkError(''); setShowBulkModal(true); }}
                style={{ marginLeft: '10px' }}
              >
                <FaUpload style={{ marginRight: '5px' }} /> Bulk Search
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search Hero Section — non-privileged users only; hide once they have results. */}
      {!isPrivilegedUser && (!searchSubmitted || combined.length === 0) && (
        <div className="search-hero">
          <h1>UN Sanctions Screening</h1>

          <div className="search-container">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder='Try "Bin Laden", "Al-Qaida", "QDi.001"…'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              {searchTerm && (
                <FaTimes className="clear-icon" onClick={clearSearch} />
              )}
              <button
                className="search-button"
                onClick={() => runSearch()}
                disabled={loading || singleAtZero}
              >
                {loading ? 'Searching…' : 'Search'}
              </button>
            </div>

            {/* Search Filters + Bulk Search */}
            <div className="search-filters">
              {FILTERS.map(f => (
                <div
                  key={f.id}
                  className={`filter-chip ${activeFilter === f.id ? 'active' : ''}`}
                  onClick={() => applyFilter(f.id)}
                >
                  {f.icon} {f.label}
                </div>
              ))}
              {canDoBulkSearch && (
                <div
                  className="filter-chip bulk-search-button"
                  onClick={bulkAtZero ? undefined : () => { setBulkError(''); setShowBulkModal(true); }}
                  style={{
                    opacity: bulkAtZero ? 0.5 : 1,
                    cursor: bulkAtZero ? 'not-allowed' : 'pointer',
                    pointerEvents: bulkAtZero ? 'none' : 'auto'
                  }}
                  title={bulkAtZero ? 'Bulk screening limit reached' : 'Bulk Search'}
                >
                  <FaUpload style={{ marginRight: '5px' }} /> Bulk Search
                </div>
              )}
            </div>

            {/* Search Tips */}
            <div className="search-tips">
              <FaInfoCircle className="info-icon" />
              <span>Tip: Search by full name, alias, or reference number (e.g. "QDi.001").</span>
            </div>

            {/* Recent searches suggestions */}
            {history.length > 0 && !searchTerm && (
              <div className="search-filters" style={{ marginTop: '1rem' }}>
                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>Recent searches:</span>
                {history.slice(0, 5).map(h => (
                  <div
                    key={h.id}
                    className="filter-chip"
                    onClick={() => applySuggestion(h.search_query, h.filter_type)}
                    title={h.result_summary}
                    style={{ backgroundColor: 'rgba(255,255,255,0.4)', cursor: 'pointer' }}
                  >
                    <span style={{ fontSize: '0.85rem' }}>
                      {h.search_query.length > 30 ? h.search_query.slice(0, 30) + '…' : h.search_query}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="alert-banner alert-banner--danger">
          <span className="alert-banner__body"><FaInfoCircle /> {error}</span>
        </div>
      )}

      {/* Loading spinner — mirrors PIPs page */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="loading-spinner"></div>
          <div className="loading-text">Searching for results...</div>
        </div>
      )}

      {/* Results */}
      <div id="search-results" className="un-results">
        {searchSubmitted && !loading && combined.length === 0 && (
          <div className="un-empty">
            <FaInfoCircle /> No matches in the UN Consolidated Sanctions List for "{searchTerm}".
          </div>
        )}

        {combined.length > 0 && (
          <>
            <div className="search-results-info" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <label
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', cursor: 'pointer' }}
                title="Select all rows on this page"
              >
                <input
                  type="checkbox"
                  checked={pageItems.length > 0 && pageItems.every(r => selectedIds.includes(recordKey(r)))}
                  onChange={e => toggleSelectPage(pageItems, e.target.checked)}
                />
                Select page
              </label>
              <div className="search-count" style={{ flex: 1, fontSize: '0.8rem' }}>
                {isPrivilegedUser
                  ? `Showing ${combined.length} of ${combinedAll.length} total UN sanctions records`
                  : `Showing ${Math.min(combined.length, (currentPage - 1) * itemsPerPage + 1)}–${Math.min(combined.length, currentPage * itemsPerPage)} of ${combined.length} match${combined.length === 1 ? '' : 'es'}`}
                {selectedIds.length > 0 && (
                  <span style={{ marginLeft: '0.75rem', fontSize: '0.85rem', color: 'var(--accent-dark,#00695c)', fontWeight: 600 }}>
                    · {selectedIds.length} selected
                    <button
                      type="button"
                      onClick={() => setSelectedIds([])}
                      style={{ marginLeft: '0.5rem', fontSize: '0.75rem', background: 'none', border: 'none', color: 'var(--text-secondary,#607d8b)', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      clear
                    </button>
                  </span>
                )}
                {!isPrivilegedUser && searchTerm && (
                  <button
                    className="clear-search-button"
                    onClick={clearSearch}
                    style={{ marginLeft: '0.75rem', fontSize: '0.75rem', padding: '0.25rem 0.55rem', whiteSpace: 'nowrap' }}
                  >
                    <FaTimes /> Clear Search
                  </button>
                )}
              </div>
              <input
                type="text"
                className="table-search-input"
                placeholder="Search table..."
                value={tableSearch}
                onChange={e => { setTableSearch(e.target.value); setCurrentPage(1); }}
              />
              <span className="un-results__split">
                <button
                  type="button"
                  className={`un-results__split-btn${activeFilter === 'individuals' ? ' un-results__split-btn--active' : ''}`}
                  onClick={() => applyFilter(activeFilter === 'individuals' ? 'all' : 'individuals')}
                  title={activeFilter === 'individuals' ? 'Show all (clear filter)' : 'Show only individuals'}
                >
                  <FaUser /> {individuals.length}
                </button>
                <button
                  type="button"
                  className={`un-results__split-btn${activeFilter === 'entities' ? ' un-results__split-btn--active' : ''}`}
                  onClick={() => applyFilter(activeFilter === 'entities' ? 'all' : 'entities')}
                  title={activeFilter === 'entities' ? 'Show all (clear filter)' : 'Show only entities'}
                >
                  <FaBuilding /> {entities.length}
                </button>
              </span>
              {/* Search Score slider — only for org users, inline next to Export. */}
              {showScoreSlider && (
                <div className="search-score-row" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span className="search-score-label">Search Score:</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={minSimilarity}
                    onChange={e => {
                      setMinSimilarity(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    style={{ width: 80 }}
                  />
                  <span className="search-score-value">{minSimilarity}</span>
                </div>
              )}
              <div className="button-group">
                <button className="export-button" onClick={exportPDF}>
                  Export PDF{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
                </button>
              </div>
            </div>

            <div className="un-card-list">
              {pageItems.map((rec, i) => renderCard(rec, i))}
            </div>

            <div className="pagination-controls">
              <div className="pagination-right">
                <div className="pagination-per-page">
                  <span>Per page:</span>
                  <select
                    value={itemsPerPage}
                    onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <div className="pagination pagination-pages">
                  <button onClick={() => setCurrentPage(c => Math.max(1, c - 1))} disabled={currentPage === 1}>← Prev</button>
                  <span className="pagination-info">Page {currentPage} of {totalPages}</span>
                  <button onClick={() => setCurrentPage(c => Math.min(totalPages, c + 1))} disabled={currentPage === totalPages}>Next →</button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* View details — pop-up modal */}
      {detailRecord && (() => {
        const record = detailRecord;
        const isInd = record._kind === 'individual';
        const title = isInd
          ? [record.first_name, record.second_name, record.third_name, record.fourth_name].filter(Boolean).join(' ')
          : record.first_name;
        return (
          <div className="modal-overlay record-detail-overlay" onClick={() => setDetailRecord(null)}>
            <div
              className="modal-content record-detail-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sanctions-detail-title"
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header" style={{ display: 'block' }}>
                <div className="un-card__title-row">
                  <span className={`un-card__badge un-card__badge--${record._kind}`}>
                    {isInd ? <FaUser /> : <FaBuilding />}
                    {isInd ? 'Individual' : 'Entity'}
                  </span>
                  <h2 id="sanctions-detail-title" className="un-card__title" style={{ margin: 0 }}>{title || 'Unnamed'}</h2>
                </div>
                <div className="un-card__meta">
                    <span className="un-meta__pill">Ref: {record.reference_number || '—'}</span>
                    <span className="un-meta__pill">{record.un_list_type || record.list_type || '—'}</span>
                    <span className="un-meta__pill">Listed: {formatDate(record.listed_on)}</span>
                    {record.has_interpol_link === 'YES' && (
                      <a href={record.interpol_link} target="_blank" rel="noopener noreferrer" className="un-meta__pill un-meta__pill--link">
                        INTERPOL notice ↗
                      </a>
                    )}
                </div>
                <button className="modal-close" onClick={() => setDetailRecord(null)} aria-label="Close">×</button>
              </div>
              <div className="modal-body record-detail-modal__body">
                {renderBody(record, true)}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Bulk Search Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => !bulkLoading && setShowBulkModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Bulk UN Sanctions Screening</h2>
              <button className="modal-close" onClick={() => !bulkLoading && setShowBulkModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-section">
                <div className="info-box" style={{ marginBottom: '1rem' }}>
                  <FaInfoCircle style={{ marginRight: '0.5rem' }} />
                  <span>Upload a CSV (or Excel) file with a single <code>full_name</code> column. Each name counts as one batch screening against your shared quota.</span>
                </div>
                <button
                  type="button"
                  className="export-button"
                  onClick={downloadBulkTemplate}
                  style={{ marginBottom: '1rem' }}
                >
                  <FaDownload style={{ marginRight: '0.5rem' }} /> Download Template
                </button>
                <form onSubmit={handleBulkSubmit}>
                  <div className="step-form-group">
                    <label htmlFor="unBulkFile">Select File</label>
                    <input
                      type="file"
                      id="unBulkFile"
                      accept=".csv,.xlsx,.xls"
                      onChange={e => {
                        const f = e.target.files && e.target.files[0];
                        setBulkFile(f || null);
                        setBulkError('');
                      }}
                      disabled={bulkLoading}
                    />
                    {bulkFile && (
                      <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                        Selected: {bulkFile.name}
                      </p>
                    )}
                  </div>
                  {bulkError && (
                    <div className="field-error" style={{ marginTop: '1rem' }}>
                      <FaInfoCircle /> {bulkError}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="export-button"
                    disabled={bulkLoading || !bulkFile}
                    style={{ marginTop: '1rem', width: '100%' }}
                  >
                    {bulkLoading ? 'Processing…' : 'Upload and Search'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unmatched bulk-search names */}
      {bulkSearchInfo && Array.isArray(bulkSearchInfo.unmatchedKeywords) && bulkSearchInfo.unmatchedKeywords.length > 0 && (
        <div style={{ marginTop: '2rem', padding: '0 1rem' }}>
          <div className="unmatched-bulk-header">UNMATCHED BULK SEARCH NAMES</div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <table className="unmatched-bulk-table">
              <thead><tr><th>Unmatched Name</th></tr></thead>
              <tbody>
                {bulkSearchInfo.unmatchedKeywords.map((kw, i) => (
                  <tr key={i}><td>{kw}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default UnSanctions;
